import { Group, ScrollArea, Stack } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterPanelGroup, FilterPanelToggle } from '../../../components/filters/filter-panel-group';
import { FilterPanelHeader } from '../../../components/filters/filter-panel-header';
import { Divider } from '../../../ui-library/divider/divider';
import { Text } from '../../../ui-library/typography/typography';
import { SmallSquareButton } from '../../../components/small-square-button/small-square-button';
import { useGoals } from '../../../api/strategy-client/strategy-client.hooks';
import { StatusLabel } from '../initiative-list/filters/status-label';
import { primaryBase, yellowBase, redBase, skyDark } from '../../../styles/design-tokens';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setStatusFilters,
  setOnlyBookmarkedFilter,
  setIncludeEmptyFilter,
} from '../../../store/strategy-store/strategy-store.actions';

export function GoalsFilters({ setShow, title }: { setShow: (show: boolean) => void; title?: string }) {
  const i18n = useTranslation();
  const { goals } = useGoals();
  const statusFiltersFromStore = useStrategyStore((state) => state.filters.status);
  const onlyBookmarkedFromStore = useStrategyStore((state) => state.filters.only_bookmarked);
  const includeEmptyFromStore = useStrategyStore((state) => state.filters.include_empty);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Initialize selectedStatuses from store
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    statusFiltersFromStore || ['on_track', 'at_risk', 'off_track', 'unknown'],
  );

  // Initialize bookmark filter from store
  const [onlyBookmarked, setOnlyBookmarked] = useState<boolean>(onlyBookmarkedFromStore || false);

  // Initialize include empty filter from store
  const [includeEmpty, setIncludeEmpty] = useState<boolean>(includeEmptyFromStore || false);

  // Sync with store when selection changes
  useEffect(() => {
    setStatusFilters(selectedStatuses);
  }, [selectedStatuses]);

  // Sync bookmark filter with store
  useEffect(() => {
    setOnlyBookmarkedFilter(onlyBookmarked);
  }, [onlyBookmarked]);

  // Sync include empty filter with store
  useEffect(() => {
    setIncludeEmptyFilter(includeEmpty);
  }, [includeEmpty]);

  // Get unique status values
  const statusOptions = [
    {
      value: 'on_track',
      label: <StatusLabel color={primaryBase} label={i18n.t('strategy.initiatives.statuses.on_track')} />,
      count: goals.filter((g) => g.status?.by_tasks?.status === 'on_track').length,
    },
    {
      value: 'at_risk',
      label: <StatusLabel color={yellowBase} label={i18n.t('strategy.initiatives.statuses.at_risk')} />,
      count: goals.filter((g) => g.status?.by_tasks?.status === 'at_risk').length,
    },
    {
      value: 'off_track',
      label: <StatusLabel color={redBase} label={i18n.t('strategy.initiatives.statuses.off_track')} />,
      count: goals.filter((g) => g.status?.by_tasks?.status === 'off_track').length,
    },
    {
      value: 'unknown',
      label: <StatusLabel color={skyDark} label={i18n.t('strategy.initiatives.statuses.unknown')} />,
      count: goals.filter((g) => !g.status?.by_tasks?.status).length,
    },
  ];

  const filterGroups = [
    {
      id: 'status',
      name: i18n.t('strategy.filters.status'),
      icon: 'filter_tilt_shift',
      options: statusOptions,
      selectedValues: selectedStatuses,
      onSelectionChange: setSelectedStatuses,
      showAllLabel: i18n.t('strategy.filters.all_statuses'),
    },
  ];

  const toggleCollapsed = (groupId: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupCollapsed = (groupId: string): boolean => {
    return collapsedGroups[groupId];
  };

  const isAllSelected = (group: (typeof filterGroups)[0]): boolean => {
    return group.selectedValues.length > 0 && group.selectedValues.length === group.options.length;
  };

  const handleAllChange = (group: (typeof filterGroups)[0], isChecked: boolean): void => {
    group.onSelectionChange(isChecked ? group.options.map((opt) => opt.value) : []);
  };

  const handleIndividualChange = (group: (typeof filterGroups)[0], value: string, isChecked: boolean): void => {
    const newValues = isChecked ? [...group.selectedValues, value] : group.selectedValues.filter((v) => v !== value);
    group.onSelectionChange(newValues);
  };

  const handleReset = () => {
    setSelectedStatuses(['on_track', 'at_risk', 'off_track', 'unknown']);
    setOnlyBookmarked(false);
    setIncludeEmpty(false);
  };

  return (
    <Stack data-bf="strategy-filter-goals" gap="md" h="100%">
      <Group justify="space-between">
        <Text size="medium" weight="medium">
          {title || i18n.t('common.strategic_goals')}
        </Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <FilterPanelHeader onResetFilters={handleReset} />
      <Divider />

      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack gap="md">
          <FilterPanelToggle
            toggle={{
              id: 'include_empty',
              name: i18n.t('strategy.filters.include_empty'),
              icon: 'folder_open',
              selectedValue: includeEmpty,
              onSelectionChange: setIncludeEmpty,
            }}
            hasDivider
          />
          <FilterPanelToggle
            toggle={{
              id: 'only_bookmarked',
              name: i18n.t('strategy.filters.bookmarked_only'),
              icon: 'bookmark_added',
              selectedValue: onlyBookmarked,
              onSelectionChange: setOnlyBookmarked,
            }}
            hasDivider
          />
          {filterGroups.map((group) => (
            <FilterPanelGroup
              key={group.id}
              group={group}
              toggleCollapsed={(group) => toggleCollapsed(group.id)}
              isCollapsed={isGroupCollapsed(group.id)}
              isAllSelected={isAllSelected(group)}
              handleAllChange={handleAllChange}
              handleIndividualChange={handleIndividualChange}
              hasDivider={false}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
